/**
 * 路由规范化工具
 *
 * 用于统一处理不同环境下的路由匹配问题：
 * - WebView App 环境下，pathname 会有 /mobile 前缀
 * - 需要去除多语言前缀
 * - 需要根据 ROUTE_MATCHING_RULES 进行路由转换
 */

import { appTypes } from "../config/seo";
import { isValidLocale, type Locale } from "./i18n.locale";

/**
 * /h5/mobile/maintab/markets/crypto -> /mobile/maintab/markets/crypto
 * /zh-tw/h5/mobile/maintab/markets/crypto -> /mobile/maintab/markets/crypto
 * 规范化 pathname，移除语言前缀和应用类型前缀，返回纯路径
 * @param pathname 原始 pathname
 * @param options 可选配置，包括是否返回多语言前缀
 * @returns 规范化后的 pathname（纯路径或包含多语言前缀的路径）
 */
export const normalizePathname = (
  pathname: string,
  includeLocale?: boolean,
): string => {
  // 分割路径段
  const segments = pathname.split("/").filter(Boolean);

  if (segments.length === 0) {
    return "/";
  }

  let startIndex = 0;
  let detectedLocale: Locale | undefined;

  // 检查第一个段是否是语言前缀
  const firstSegment = segments[0] || "";
  if (isValidLocale(firstSegment)) {
    detectedLocale = firstSegment;
    startIndex = 1;
  }

  // 检查下一个段是否是应用类型前缀
  if (startIndex < segments.length && appTypes.includes(segments[startIndex])) {
    startIndex += 1;
  }

  // 组合剩余段为纯路径
  const remainingSegments = segments.slice(startIndex);
  const purePath =
    remainingSegments.length > 0 ? "/" + remainingSegments.join("/") : "/";

  // 根据选项决定是否包含多语言前缀
  // 注意：英语（en）通常不需要在路径中包含语言前缀
  if (includeLocale && detectedLocale && detectedLocale !== "en") {
    return `/${detectedLocale}${purePath}`;
  }

  return purePath;
};

/**
 * 检查 pathname 是否包含指定路径（考虑多语言和 mobile 前缀）
 * @param pathname 当前路径
 * @param targetPath 目标路径
 * @returns 是否包含
 */
